import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { appSettingsService } from "@/lib/api-service";
import { toast } from "sonner";
import type { AppSettings } from "@/types/app-settings.types";
import { SectionHeading } from "@/components/ui/section-heading";
import { Plus, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

// Form validation schema
const departmentsFormSchema = z.object({
  departments: z.array(
    z.object({
      name: z
        .string()
        .min(1, "Department name is required")
        .min(2, "Department name must be at least 2 characters")
        .max(50, "Department name must be less than 50 characters"),
    })
  ),
  newDepartment: z.string(),
});

type DepartmentsFormData = z.infer<typeof departmentsFormSchema>;

export function DepartmentsTab() {
  const queryClient = useQueryClient();

  // Initialize form with react-hook-form
  const form = useForm<DepartmentsFormData>({
    resolver: zodResolver(departmentsFormSchema),
    defaultValues: {
      departments: [],
      newDepartment: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "departments",
  });

  // Fetch app settings using useQuery
  const {
    data: settings,
    isLoading,
    isError,
  } = useQuery<AppSettings>({
    queryKey: ["app-settings"],
    queryFn: () => appSettingsService.getAll(),
  });

  // Save departments mutation
  const saveMutation = useMutation({
    mutationFn: async (departments: string[]) => {
      return appSettingsService.save("department_list", departments);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-settings"] });
      toast.success("Departments saved successfully");
      form.reset({
        departments: form.getValues("departments"),
        newDepartment: "",
      });
    },
    onError: () => {
      toast.error("Failed to save departments");
    },
  });

  const userDepartments = settings?.department_list || [];

  // Initialize form when data loads
  useEffect(() => {
    if (userDepartments.length > 0 && fields.length === 0) {
      const departmentObjects = userDepartments.map((name) => ({ name }));
      form.reset({
        departments: departmentObjects,
        newDepartment: "",
      });
    }
  }, [userDepartments, fields.length, form]);

  const handleAddDepartment = () => {
    const newDepartmentValue = form.getValues("newDepartment").trim();
    if (newDepartmentValue) {
      // Check for duplicates
      const existingNames = fields.map((field) => field.name.toLowerCase());
      if (!existingNames.includes(newDepartmentValue.toLowerCase())) {
        append({ name: newDepartmentValue });
        form.setValue("newDepartment", "");
      } else {
        toast.error("Department already exists");
      }
    }
  };

  const handleDeleteDepartment = (index: number) => {
    remove(index);
  };

  const handleSave = () => {
    const departmentNames = fields.map((field) => field.name);
    saveMutation.mutate(departmentNames);
  };

  // Check if form has changes
  const hasChanges = form.formState.isDirty;

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Failed to load settings
      </div>
    );
  }

  return (
    <Card className="!bg-transparent">
      <CardHeader>
        <SectionHeading
          title="Departments"
          subtitle="Manage company departments. These departments are used for employee categorization and project management."
          actions={
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending || !hasChanges}
            >
              {saveMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          }
        />
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <div className="space-y-6">
            {/* Add new department section */}
            <div className="space-y-3">
              <label className="text-sm font-medium">Add New Department</label>
              <div className="flex items-center gap-2">
                <FormField
                  control={form.control}
                  name="newDepartment"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter department name"
                          disabled={saveMutation.isPending}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddDepartment();
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  onClick={handleAddDepartment}
                  disabled={
                    !form.watch("newDepartment").trim() ||
                    saveMutation.isPending
                  }
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>

            {/* Departments table */}
            <div className="space-y-3">
              <label className="text-sm font-medium">
                Current Departments ({fields.length})
              </label>

              {fields.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                  No departments added yet. Add your first department above.
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="!bg-muted/50">
                        <TableHead>Department Name</TableHead>
                        <TableHead className="w-32">Status</TableHead>
                        <TableHead className="w-16 text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fields.map((field, index) => (
                        <TableRow key={field.id}>
                          <TableCell>
                            <span className="font-medium">{field.name}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">
                              Active
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              type="button"
                              onClick={() => handleDeleteDepartment(index)}
                              disabled={saveMutation.isPending}
                              className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>
  );
}
